import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProjectWiseCost, getMonitoringProjectList } from "../../redux/adminUnitReportSlice/adminUnitReportSlice";
import { getAllUnits } from "../../redux/unitSlice/unitSlice";
import CustomTable from '../../components/Common/CustomTable/CustomTable';
import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";
import PMGProjectIcon from "../../assets/icons/pmg-project.svg";
import {
    Building2,
    Sigma,
    BarChart3,
    ChevronRight,
    ChevronDown,
    Loader2,
} from "lucide-react";

const PROJECT_TYPE_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "EPC", value: "EPC" },
    { label: "BTG", value: "BTG" },
    { label: "OTHER", value: "OTHER" },
];

const COST_LEVEL_OPTIONS = [
    { label: "Direct Material", value: "DIRECT_MATERIAL" },
    { label: "Conversion Cost", value: "CONVERSION_COST" },
    { label: "Factory Cost Level", value: "TOTAL_COST" },
];

const VALUE_MODE_OPTIONS = [
    { label: "Crore", value: "CRORE" },
    { label: "Percent", value: "PERCENT" },
];

// Only 2 top-level time filter modes now. Quarter is NOT a separate mode —
// it's a refinement that appears inside YEAR mode once a Year is picked
// (see selectedQuarter below), so the user never has to pick a mode called
// "Quarterly" — just Year -> FY -> optionally a quarter within that FY.
const TIME_FILTER_OPTIONS = [
    { label: "Overall", value: "OVERALL" },
    { label: "Year Wise", value: "YEAR" },
];

const toNumber = (val) => {
    // Values from the API can come formatted with thousands separators
    // (e.g. "10,518.00"). parseFloat stops at the first comma, so
    // parseFloat("10,518.00") would wrongly return 10 instead of 10518.
    // Strip commas (and any stray whitespace) before parsing.
    const cleaned = String(val ?? "").replace(/,/g, "").trim();
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
};

// Builds a normalized "IO_NO-REV_NO" key so matching works regardless of
// whether the two APIs send numbers, strings, extra whitespace, or missing REV_NO.
const makeProjectKey = (ioNo, revNo) => {
    const io = String(ioNo ?? "").trim();
    const rev = String(revNo ?? "0").trim();
    return `${io}-${rev}`;
};

// Appends a "%" suffix to a displayed cell value when Value Mode is Percent.
// Leaves empty/undefined values untouched so blank cells stay blank.
const formatCellValue = (val, valueMode) => {
    if (val === undefined || val === null || val === "") return val;
    return valueMode === "PERCENT" ? `${val}%` : val;
};

// The financial year (Apr -> Mar) that "today" falls in, expressed as its
// starting calendar year, e.g. April 2026 - March 2027 -> 2026.
const getCurrentFYStart = () => {
    const today = new Date();
    return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const CURRENT_FY_START = getCurrentFYStart();

// The real-world current FY as a "SSSS-EEEE" key, e.g. "2026-2027".
const CURRENT_FY_KEY = `${CURRENT_FY_START}-${CURRENT_FY_START + 1}`;

// FLAG: controls what the "Upto March" column is anchored to.
// - true  -> anchored to the REAL current FY (CURRENT_FY_KEY). The label/
//            value do NOT change when the user picks a different Year in
//            the filter — they only advance once the calendar actually
//            rolls into a new FY (i.e. once CURRENT_FY_START itself
//            changes on page load). This is the requested behavior:
//            "Upto March 27" only appears once we're actually in April
//            2027, regardless of which FY is selected in the dropdown.
// - false -> anchored to whichever FY is selected in the filter dropdown
//            (previous behavior — label/value shift as the user changes
//            the Year filter).
const UPTO_MARCH_ANCHOR_TO_CURRENT_FY = true;

// Resolves which FY key "Upto March" should actually be computed against,
// based on the flag above.
const getUptoMarchAnchorYear = (selectedYear) =>
    UPTO_MARCH_ANCHOR_TO_CURRENT_FY ? CURRENT_FY_KEY : selectedYear;

// "Q1-2026" -> "2026-2027"
const getFYFromQuarterKey = (key) => {
    const match = /^Q[1-4]-(\d{4})$/.exec(key);
    if (!match) return null;
    const fyStart = Number(match[1]);
    return `${fyStart}-${fyStart + 1}`;
};

const getQuarterLabel = (key) => key.split("-")[0];

// Given a "SSSS-EEEE" FY key, returns its start year as a number, e.g.
// "2026-2027" -> 2026. Used to compare FYs chronologically.
const getFYStartYear = (fyKey) => Number(String(fyKey).split("-")[0]);

// "Upto March" figure for a record (project / unit / group total / grand
// total), as of the selected FY. Definition (per business requirement):
// upto (a baseline/brought-forward figure the API returns) + the sum of
// every financial year in record.financial_years that falls strictly
// before the selected FY.
//
// NOTE: the API nests "upto" AS A KEY INSIDE financial_years (e.g.
// financial_years: { "2024-2025": ..., "upto": ... }) rather than as a
// separate top-level field on the record — so it's read from there, and
// explicitly excluded from the FY-comparison loop below (its key isn't a
// real "SSSS-EEEE" FY, so getFYStartYear("upto") would be NaN anyway, but
// skipping it explicitly keeps the intent obvious).
//
// e.g. selectedYear = "2026-2027" ->
//      upto + FY2023-24 + FY2024-25 + FY2025-2026
// e.g. selectedYear = "2027-2028" ->
//      upto + FY2023-24 + FY2024-25 + FY2025-2026 + FY2026-2027
const getUptoMarchValue = (record, selectedYear) => {
    if (!record || !selectedYear) return "0.00";

    const selectedStartYear = getFYStartYear(selectedYear);
    let sum = toNumber(record.financial_years?.upto);

    Object.entries(record.financial_years || {}).forEach(([fyKey, val]) => {
        if (fyKey === "upto") return;
        if (getFYStartYear(fyKey) < selectedStartYear) {
            sum += toNumber(val);
        }
    });

    return sum.toFixed(2);
};

// Dynamic label for the "Upto March" column, e.g. selectedYear
// "2026-2027" -> "Upto March 2026" (March 2026 is the FY-end boundary
// immediately before the selected FY starts, in April 2026).
const getUptoMarchLabel = (selectedYear) =>
    selectedYear ? `Upto March ${getFYStartYear(selectedYear)}` : "Upto March";

const DMProjectWiseReport = () => {
    const dispatch = useDispatch();

    // Backend-dependent filters
    const [projectType, setProjectType] = useState("ALL");
    const [costLevel, setCostLevel] = useState("DIRECT_MATERIAL");
    const [valueMode, setValueMode] = useState("PERCENT");

    // Master, unfiltered list of projects — used only to populate the
    // "Projects Filter" dropdown. Unlike allProjectsFlat, this does NOT
    // shrink when the API response is filtered down to selected projects.
    const [masterProjects, setMasterProjects] = useState([]);

    // Holds "IO_NO-REV_NO" keys selected in the multi-select (strings, since
    // that's what the select control can compare/highlight). The actual
    // {IO_NO, REV_NO} objects sent to the API are derived from these keys
    // via projectKeyToProject below.
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);
    // [] = All Projects
    // ["2562-0", "3107-0", ...] = Selected Projects

    // Frontend-only filters. YEAR mode doesn't change the table shape (still
    // Project Name | Unit columns | Total Cost, same as OVERALL) — it just
    // changes which period's figure is shown in each cell. selectedQuarter
    // is a refinement WITHIN Year mode: "" = Full Year, else a specific
    // quarter belonging to selectedYear.
    const [timeFilter, setTimeFilter] = useState("OVERALL");
    const [selectedYear, setSelectedYear] = useState(""); // FY key, e.g. "2026-2027"
    const [selectedQuarter, setSelectedQuarter] = useState(""); // "" = Full Year, else e.g. "Q1-2026"
    const [selectedUnits, setSelectedUnits] = useState([]);

    // Which group sections (EPC / BTG / OTHER) are expanded.
    const [expandedGroups, setExpandedGroups] = useState({});

    const { projectWiseCost, gettingProjectWiseCost, monitoringProjectList } = useSelector(
        (state) => state.unitReport
    );
    const { allUnitsList } = useSelector((state) => state.unit);

    const reportData = projectWiseCost?.data ?? projectWiseCost ?? null;

    const groups = reportData?.groups ?? [];
    const yearLabels = reportData?.labels ?? [];
    const quarterLabels = reportData?.quarterly_labels ?? [];

    const fetchReport = () => {
        const projectsPayload = selectedProjectKeys
            .map((key) => projectKeyToProject.get(key))
            .filter(Boolean)
            .map(({ IO_NO, REV_NO }) => ({ IO_NO, REV_NO }));

        dispatch(
            getProjectWiseCost({
                PROJECT_TYPE: projectType,
                COST_LEVEL: costLevel,
                VALUE_MODE: valueMode,
                projects: projectsPayload,
            })
        );
    };

    const fetchMonitoringProjects = () => {
        dispatch(
            getMonitoringProjectList({
                PROJECT_TYPE: projectType,
            })
        );
    };

    useEffect(() => {
        dispatch(getAllUnits());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial load with default filters.
    useEffect(() => {

        setSelectedProjectKeys([]); // reset so masterProjects can repopulate for the new type
        fetchReport();
        fetchMonitoringProjects();

    }, [projectType]);

    // Re-fetch whenever a backend-dependent filter changes.
    // NOTE: timeFilter / selectedYear / selectedQuarter are intentionally
    // NOT here — the API already returns every year/quarter in one payload,
    // so switching period is purely a frontend re-slice of existing data.
    // Skip the very first run — the [projectType] effect above already
    // fires the initial fetch, so this effect would otherwise double it.
    const isProjectWiseCostMount = useRef(true);

    useEffect(() => {
        if (isProjectWiseCostMount.current) {
            isProjectWiseCostMount.current = false;
            return;
        }

        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costLevel, valueMode, selectedProjectKeys.join(",")]);

    // Reset expand state (collapsed by default) whenever a fresh set of groups loads.
    useEffect(() => {
        const initial = {};
        groups.forEach((group) => {
            initial[group.PROJECT_TYPE] = true;
        });
        setExpandedGroups(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportData]);

    const toggleGroup = (type) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    // Every project across every group, used to populate the "Selected Projects" dropdown.
    // Only capture the full project list when the response was NOT filtered
    // by a project selection (i.e. selectedProjectKeys is empty). This keeps
    // the dropdown's option list stable even after the table is filtered
    // down to a subset of projects.
    useEffect(() => {
        if (selectedProjectKeys.length === 0 && groups.length) {
            setMasterProjects((prev) => {
                // Merge into existing cache (keyed by IO_NO-REV_NO) instead of
                // overwriting, so projects fetched under a previous
                // projectType stay available if the user switches back.
                const map = new Map(
                    prev.map((project) => [makeProjectKey(project.IO_NO, project.REV_NO), project])
                );

                groups.forEach((group) => {
                    group.projects?.forEach((project) => {
                        map.set(makeProjectKey(project.IO_NO, project.REV_NO), {
                            IO_NO: project.IO_NO,
                            REV_NO: project.REV_NO,
                            PROJ_NAME: project.PROJ_NAME,
                            PROJECT_TYPE: group.PROJECT_TYPE,
                        });
                    });
                });

                return Array.from(map.values());
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]);

    // What's actually shown in the "Projects Filter" dropdown — narrowed to
    // the currently selected Project Type, without discarding the other
    // types from masterProjects (they stay cached for when the user
    // switches back).
    const visibleProjectsForFilter = useMemo(() => {
        if (projectType === "ALL") return masterProjects;
        return masterProjects.filter((project) => project.PROJECT_TYPE === projectType);
    }, [masterProjects, projectType]);

    // Maps a "IO_NO-REV_NO" key (what the multi-select stores) back to the
    // full { IO_NO, REV_NO, PROJ_NAME } object (what the API needs).
    const projectKeyToProject = useMemo(() => {
        const map = new Map();
        masterProjects.forEach((project) => {
            map.set(makeProjectKey(project.IO_NO, project.REV_NO), project);
        });
        return map;
    }, [masterProjects]);

    // Union of every unit (MNF_UNIT + UNIT_NAME) seen across all projects in the
    // currently loaded response. These are the dynamic middle columns —
    // used in Overall, and in Year mode (Full Year or a specific quarter)
    // alike.
    const unitColumns = useMemo(() => {
        const seen = new Map();
        groups.forEach((group) => {
            group.projects?.forEach((project) => {
                project.units?.forEach((unit) => {
                    if (!seen.has(unit.MNF_UNIT)) {
                        seen.set(unit.MNF_UNIT, {
                            MNF_UNIT: unit.MNF_UNIT,
                            UNIT_NAME: unit.UNIT_NAME,
                        });
                    }
                });
            });
        });

        const unsorted = Array.from(seen.values());
        const list = allUnitsList?.data?.data ?? allUnitsList?.data ?? allUnitsList ?? [];
        const orderMap = new Map();
        list.forEach((u, index) => {
            orderMap.set(u.MNF_UNIT, index);
        });

        return unsorted.sort((a, b) => {
            const indexA = orderMap.has(a.MNF_UNIT) ? orderMap.get(a.MNF_UNIT) : Infinity;
            const indexB = orderMap.has(b.MNF_UNIT) ? orderMap.get(b.MNF_UNIT) : Infinity;
            return indexA - indexB;
        });
    }, [groups, allUnitsList]);

    const unitOptions = useMemo(() => {
        const list = allUnitsList?.data?.data ?? allUnitsList?.data ?? allUnitsList ?? [];
        return list.map((u) => ({
            label: u.UNIT_DISPLAY_NAME,
            value: u.MNF_UNIT,
        }));
    }, [allUnitsList]);

    const visibleUnitColumns = useMemo(() => {
        if (selectedUnits.length === 0) return unitColumns;
        return unitColumns.filter((unit) => selectedUnits.includes(unit.MNF_UNIT));
    }, [unitColumns, selectedUnits]);

    const findUnitByMnfCode = (project, mnfUnit) =>
        project?.units?.find((unit) => unit.MNF_UNIT === mnfUnit) || null;

    // FY labels from the current financial year onward — populate the Year
    // dropdown (shown for YEAR mode).
    const visibleYearLabels = useMemo(() => {
        return yearLabels
            .filter((fy) => Number(fy.split("-")[0]) >= CURRENT_FY_START)
            .sort();
    }, [yearLabels]);

    // Quarter keys grouped under their parent FY, e.g.
    // { "2026-2027": ["Q1-2026", "Q2-2026", "Q3-2026", "Q4-2026"] }.
    // Used to populate the Quarter dropdown, scoped to whichever FY is
    // currently selected.
    const groupedQuarterKeys = useMemo(() => {
        const map = {};

        quarterLabels.forEach((key) => {
            const fy = getFYFromQuarterKey(key);
            if (!fy || Number(fy.split("-")[0]) < CURRENT_FY_START) return;

            if (!map[fy]) map[fy] = [];
            map[fy].push(key);
        });

        Object.keys(map).forEach((fy) => {
            map[fy].sort((a, b) => a.localeCompare(b));
        });

        return map;
    }, [quarterLabels]);

    // Auto-pick a default Year the moment the user switches into YEAR mode,
    // so the table isn't blank while they haven't touched the dropdown yet.
    useEffect(() => {
        if (timeFilter !== "YEAR") return;
        if (!selectedYear && visibleYearLabels.length) {
            setSelectedYear(visibleYearLabels[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeFilter, visibleYearLabels]);

    // Changing the Year invalidates whatever Quarter was picked (it belongs
    // to a different FY) — reset to "Full Year" rather than re-defaulting to
    // Q1, since Full Year is the natural landing state right after picking
    // a year; the user opts into a specific quarter from there.
    useEffect(() => {
        setSelectedQuarter("");
    }, [selectedYear]);

    // Central accessor: given a record that carries { total, financial_years,
    // quarters } (a project, a unit, or a group's "total" object), return
    // the figure for whichever period is currently selected. This is the
    // single place that decides what OVERALL / Full-Year / Quarter mean.
    const getPeriodValue = (record) => {
        if (!record) return "0.00";
        if (timeFilter === "YEAR") {
            if (selectedQuarter) {
                return record.quarters?.[selectedQuarter] ?? "0.00";
            }
            return record.financial_years?.[selectedYear] ?? "0.00";
        }
        return record.total ?? "0.00";
    };

    // "Upto March" column is only meaningful once a Year is picked — it
    // appears for both Full Year and a specific Quarter within that Year,
    // since the cumulative-through-prior-years figure doesn't depend on
    // which quarter of the *current* FY is selected.
    const showUptoMarchColumn = timeFilter === "YEAR" && !!selectedYear && valueMode !== "PERCENT";

    const columns = useMemo(() => {
        const projectNameColumn = {
            title: "Project Name",
            dataIndex: "projectName",
            key: "projectName",
            fixed: "left",
            width: 260,
            render: (text, record) => {
                if (record.isGrandTotal) {
                    return (
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontWeight: 800,
                                fontSize: 16,
                            }}
                        >
                            <BarChart3
                                size={18}
                                color="#155EEF"
                            />

                            {text}
                        </span>
                    );
                }
                if (record.isHeading) {
                    const isOpen = !!expandedGroups[record.groupType];
                    return (
                        <span
                            onClick={() => toggleGroup(record.groupType)}
                            style={{
                                cursor: "pointer",
                                fontWeight: 800,
                                fontSize: 16,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            <span style={{ display: "inline-block", fontWeight: 900, width: 16 }}>
                                {
                                    isOpen ?
                                        <ChevronDown size={16} /> :
                                        <ChevronRight size={16} />
                                }
                            </span>
                            <Building2
                                size={18}
                                color="#155EEF"
                            />
                            {text}
                        </span>
                    );
                }

                if (record.isGroupTotal) {
                    return (
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontWeight: 700,
                            }}
                        >
                            <Sigma
                                size={18}
                                color="#155EEF"
                            />

                            {text}
                        </span>
                    );
                }

                // Regular project row. Units are shown as columns in every
                // mode, so there's no per-project drill-down.
                return (
                    <span>
                        {text}
                    </span>
                );
            },
        };

        const cellRender = (val, record) =>
            record.isHeading ? "" : formatCellValue(val, valueMode);

        // Label the Total Cost column with whichever period is active, so
        // it's clear the figure isn't the all-time total once a Year (and
        // optionally a Quarter within it) is picked.
        let totalCostSuffix = "";
        if (timeFilter === "YEAR" && selectedYear) {
            totalCostSuffix = selectedQuarter
                ? ` (${getQuarterLabel(selectedQuarter)} FY ${selectedYear})`
                : ` (FY ${selectedYear})`;
        }

        const totalCostColumn = {
            title: `${valueMode === "PERCENT" ? "Project Level %" : "Project Level"}${totalCostSuffix}`,
            dataIndex: "total",
            key: "total",
            width: 170,
            align: "right",
            fixed: "left",
            render: cellRender,
        };

        // "Upto March <year>" — cumulative figure through the end of the FY
        // immediately preceding the selected one. Shown in Year mode
        // (Full Year or any Quarter within it), positioned just before the
        // Total Cost column.
        // const uptoMarchColumn = showUptoMarchColumn
        //     ? [
        //         {
        //             title: `${getUptoMarchLabel(getUptoMarchAnchorYear(selectedYear))}${valueMode === "PERCENT" ? " %" : ""}`,
        //             dataIndex: "uptoMarch",
        //             key: "uptoMarch",
        //             width: 170,
        //             align: "right",
        //             render: cellRender,
        //         },
        //     ]
        //     : [];

        return [
            projectNameColumn,
            // Total Cost is only meaningful when every unit is included —
            // once the user narrows to specific units, it's hidden so the
            // remaining unit columns aren't mistaken for summing to it.
            ...(selectedUnits.length === 0 ? [totalCostColumn] : []),
            ...visibleUnitColumns.map((unit) => ({
                title: unit.UNIT_NAME,
                dataIndex: unit.MNF_UNIT,
                key: unit.MNF_UNIT,
                width: 150,
                align: "right",
                render: cellRender,
            })),
            // ...uptoMarchColumn,
        ];
    }, [visibleUnitColumns, expandedGroups, valueMode, timeFilter, selectedYear, selectedQuarter, showUptoMarchColumn, selectedUnits]);

    // Normalized set of "IO_NO-REV_NO" keys returned by the monitoring API.
    // Handles a few common response shapes: a bare array, { data: [...] },
    // or { data: { projects: [...] } }.
    const monitoringProjectMap = useMemo(() => {
        let list = monitoringProjectList;

        if (list && !Array.isArray(list)) {
            list = list.data ?? list.projects ?? list.data?.projects ?? [];
        }

        if (!Array.isArray(list)) {
            list = [];
        }

        const map = new Set(
            list.map((item) => makeProjectKey(item.IO_NO, item.REV_NO))
        );

        return map;
    }, [monitoringProjectList]);

    const tableData = useMemo(() => {
        const rows = [];

        // Unit columns are the same set in every mode — only the value
        // pulled per cell (via getPeriodValue) changes.
        const valueKeys = visibleUnitColumns.map((column) => column.MNF_UNIT);

        const grandValueTotals = {};
        valueKeys.forEach((key) => { grandValueTotals[key] = 0; });
        let grandTotal = 0;
        let grandUptoMarch = 0;

        groups.forEach((group) => {
            // When a specific project type is selected (EPC / BTG / OTHER),
            // only render that group's section — the other groups are hidden
            // entirely (not just collapsed).
            if (projectType !== "ALL" && group.PROJECT_TYPE !== projectType) {
                return;
            }

            const isOpen = !!expandedGroups[group.PROJECT_TYPE];

            const filteredProjects = (group.projects?.filter((project) => {

                if (projectType === "ALL") {
                    return true;
                }

                return monitoringProjectMap.has(
                    makeProjectKey(project.IO_NO, project.REV_NO)
                );

            }) || []).filter((project) => {
                if (selectedUnits.length === 0) return true;
                return (project.units || []).some((u) => selectedUnits.includes(u.MNF_UNIT));
            });

            // Group heading row (always shown, toggles the projects below it).
            rows.push({
                key: `${group.PROJECT_TYPE}-heading`,
                projectName: `${group.PROJECT_TYPE}(${filteredProjects.length})`,
                groupType: group.PROJECT_TYPE,
                isHeading: true,
            });

            if (!isOpen) {
                return;
            }

            // Running totals for this group, computed on the frontend by
            // summing each project's value for every unit column.
            const groupValueTotals = {};
            valueKeys.forEach((key) => { groupValueTotals[key] = 0; });
            let groupTotalSum = 0;
            let groupUptoMarchSum = 0;

            filteredProjects.forEach((project) => {
                const projectKey = makeProjectKey(project.IO_NO, project.REV_NO);

                const row = {
                    key: projectKey,
                    projectName: project.PROJ_NAME,
                };

                valueKeys.forEach((key) => {
                    const unit = findUnitByMnfCode(project, key);
                    const value = getPeriodValue(unit);
                    row[key] = value;
                    groupValueTotals[key] += toNumber(value);
                    grandValueTotals[key] += toNumber(value);
                });

                row.total = getPeriodValue(project);
                groupTotalSum += toNumber(row.total);
                grandTotal += toNumber(row.total);

                if (showUptoMarchColumn) {
                    row.uptoMarch = getUptoMarchValue(project, getUptoMarchAnchorYear(selectedYear));
                    groupUptoMarchSum += toNumber(row.uptoMarch);
                    grandUptoMarch += toNumber(row.uptoMarch);
                }

                rows.push(row);
            });

            // Group total row. The API's group.total now carries a real
            // per-unit breakdown too (group.total.units), backend-computed —
            // including correct group-level PERCENT figures, which a simple
            // FE sum of project percentages could never give (e.g. four 25%
            // quarters don't sum to a real 100%). So whenever group.total
            // (and its units array) is present, every figure in this row is
            // read straight from the backend via getPeriodValue, which
            // already maps to whatever period is active (Overall / Full
            // Year / a specific Quarter). FE-summed values remain only as a
            // fallback for whichever figures the backend didn't supply.
            const hasBackendGroupTotal = !!group.total;
            const hasBackendGroupUnits = !!group.total?.units?.length;

            const totalRow = {
                key: `${group.PROJECT_TYPE}-Projects`,
                projectName: `${group.PROJECT_TYPE} Projects`,
                isGroupTotal: true,
            };

            valueKeys.forEach((key) => {
                const backendUnit = hasBackendGroupUnits
                    ? findUnitByMnfCode(group.total, key)
                    : null;

                totalRow[key] = backendUnit
                    ? getPeriodValue(backendUnit)
                    : groupValueTotals[key].toFixed(2);
            });

            totalRow.total = hasBackendGroupTotal
                ? getPeriodValue(group.total)
                : groupTotalSum.toFixed(2);

            if (showUptoMarchColumn) {
                const anchorYear = getUptoMarchAnchorYear(selectedYear);
                totalRow.uptoMarch = hasBackendGroupTotal
                    ? getUptoMarchValue(group.total, anchorYear)
                    : groupUptoMarchSum.toFixed(2);
            }

            // Backend group totals (including Percent mode) are now
            // meaningful figures, so the Total row is always shown for
            // every Project Type — no more hiding it in Percent mode.
            rows.push(totalRow);
        });

        const grandTotalRow = {
            key: "grand-total",
            projectName: "GRAND TOTAL",
            isGrandTotal: true,
        };

        valueKeys.forEach((key) => {
            grandTotalRow[key] = grandValueTotals[key].toFixed(2);
        });

        grandTotalRow.total = grandTotal.toFixed(2);

        if (showUptoMarchColumn) {
            grandTotalRow.uptoMarch = grandUptoMarch.toFixed(2);
        }

        // Percent mode: a summed grand total of percentages isn't a
        // meaningful figure, so it's hidden from the table.
        return valueMode === "PERCENT"
            ? [...rows]
            // : [grandTotalRow, ...rows];
            : [...rows];
    }, [
        groups,
        visibleUnitColumns,
        timeFilter,
        selectedYear,
        selectedQuarter,
        expandedGroups,
        projectType,
        monitoringProjectMap,
        valueMode,
        showUptoMarchColumn,
        selectedUnits,
    ]);

    const quartersForSelectedYear = groupedQuarterKeys[selectedYear] || [];

    return (
        <div className="pb-14">
            <div className="px-5 flex flex-col gap-6 pt-8">
                {/* HEADER */}

                <div className="flex gap-3">
                    <img
                        src={PMGProjectIcon}
                        alt="icon"
                    />

                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            DM Analytics - Project Wise Report
                        </div>

                        <div className="text-gray-500 text-sm">
                            * All figures are in Rs Crores or in Percent depending on selected filter.
                        </div>
                    </div>
                </div>
                {/* Filters */}
                <div className="flex flex-col gap-6 mt-2">

                    <div
                        className=" flex flex-wrap items-start justify-start gap-4 p-4 bg-[#F9FAFB] border border-[#EAECF0] rounded-xl"
                    >

                        {/* Type of Project */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Type of Project
                            </span>

                            <CustomSelectWithFilter
                                data={PROJECT_TYPE_OPTIONS}
                                value={projectType}
                                onChange={setProjectType}
                                placeholder="Type of Project"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>


                        {/* Cost Level */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Cost Level
                            </span>

                            <CustomSelectWithFilter
                                data={COST_LEVEL_OPTIONS}
                                value={costLevel}
                                onChange={setCostLevel}
                                placeholder="Cost Level"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>


                        {/* Value Mode */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Value Mode
                            </span>

                            <CustomSelectWithFilter
                                data={VALUE_MODE_OPTIONS}
                                value={valueMode}
                                onChange={setValueMode}
                                placeholder="Value Mode"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>


                        {/* Time Filter */}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Time Filter
                            </span>

                            <CustomSelectWithFilter
                                data={TIME_FILTER_OPTIONS}
                                value={timeFilter}
                                onChange={setTimeFilter}
                                placeholder="Time Filter"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Year — appears for Year Wise mode */}
                        {timeFilter === "YEAR" && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">
                                    Year
                                </span>

                                <CustomSelectWithFilter
                                    data={visibleYearLabels.map((year) => ({
                                        label: `FY ${year}`,
                                        value: year,
                                    }))}
                                    value={selectedYear}
                                    onChange={setSelectedYear}
                                    placeholder="Select Year"
                                    className="w-[180px]"
                                    allowClear={false}
                                />
                            </div>
                        )}

                        {/* Quarter — appears as soon as a Year is selected;
                            refines the same Year-mode table instead of
                            requiring a separate "Quarterly" time filter. */}
                        {timeFilter === "YEAR" && selectedYear && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">
                                    Quarter
                                </span>

                                <CustomSelectWithFilter
                                    data={[
                                        { label: "Full Year", value: "" },
                                        ...quartersForSelectedYear.map((key) => ({
                                            label: getQuarterLabel(key),
                                            value: key,
                                        })),
                                    ]}
                                    value={selectedQuarter}
                                    onChange={setSelectedQuarter}
                                    placeholder="Full Year"
                                    className="w-[140px]"
                                    // allowClear={false}
                                />
                            </div>
                        )}

                        <div className="flex flex-col gap-1">

                            <span className="text-xs font-medium text-[#667085]">
                                Projects Filter
                            </span>

                            <CustomMultiSelect
                                data={visibleProjectsForFilter.map((project) => ({
                                    label: project.PROJ_NAME,
                                    value: makeProjectKey(project.IO_NO, project.REV_NO),
                                }))}
                                value={selectedProjectKeys}
                                onChange={setSelectedProjectKeys}
                                placeholder="All Projects"
                                className="w-[280px]"
                            />

                        </div>

                        {/* Unit Filter */}
                        <div className="flex flex-col gap-1">

                            <span className="text-xs font-medium text-[#667085]">
                                Unit Filter
                            </span>

                            <CustomMultiSelect
                                data={unitOptions}
                                value={selectedUnits}
                                onChange={setSelectedUnits}
                                placeholder="All Units"
                                className="w-[280px]"
                            />

                        </div>
                    </div>
                </div>

                {/* Table */}
                {gettingProjectWiseCost ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-8 w-8 animate-spin text-[#155EEF]" />
                    </div>
                ) : (
                    <div className="">
                        <CustomTable
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            scroll={{
                                x: "max-content",
                                y: 600,
                            }}
                            rowClassName={(record) => {
                                if (record.isGrandTotal)
                                    return "dm-grand-total-row";
                                if (record.isHeading) return "dm-group-heading-row";
                                if (record.isGroupTotal) return "dm-group-total-row";
                                return "";
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DMProjectWiseReport;
